// How a section is painted. Pure — no MapLibre, no DOM — so the rules that keep
// the map and the legend agreeing can actually be tested.
//
// The map draws all 5 508 sections from ONE GeoJSON source. Per-section colour
// lives in MapLibre feature-state ({fill, stroke}), so a re-colour (time
// slider, stay length, filters) only updates state — the geometry is never
// re-tiled. The layer specs below read that state.
import { STATUS_COLOR, CATEGORY_COLOR } from '../../../shared/model.js'

const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
const toHex = ([r, g, b]) =>
  `#${((Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).padStart(6, '0')}`

/** Darken a hex colour — polygon strokes on a LIGHT basemap, so same-colour
 *  neighbours keep visible boundaries and free/paid isn't hue-only (CVD). */
export function darken(hex, f = 0.62) {
  return toHex(rgb(hex).map((v) => v * f))
}

/** Lighten toward white — strokes on a DARK basemap, where a darkened outline
 *  would vanish. Keeps even the deepest price colour outlined on near-black. */
export function lighten(hex, f = 0.45) {
  return toHex(rgb(hex).map((v) => v + (255 - v) * f))
}

// Paid now but flipping free within this many minutes shows amber, not red —
// "wait 20 minutes and it's free" is a decision Prague drivers actually make.
export const SOON_MIN = 30

/**
 * Fill opacity for sections, every colour mode. Translucent on purpose so the
 * streets, parks and labels underneath stay readable. The legend paints its
 * swatches at this exact alpha (legendSwatch), so the key matches the map
 * instead of showing a solid colour the map never paints.
 *
 * Dark mode fills more opaquely: blended 60/40 into a near-black basemap the
 * ramp's dark end sinks into the background. At 0.85 the price ramp keeps a
 * worst adjacent ΔE2000 of 8.2 across normal + deutan/protan/tritan vision
 * (measured), better than any lifted "dark-mode ramp" — so both themes share
 * one ramp and one meaning, and only the alpha and the outline change.
 */
export const MAP_FILL_ALPHA = 0.6
export const MAP_FILL_ALPHA_DARK = 0.85
export const fillAlpha = (theme) => (theme === 'dark' ? MAP_FILL_ALPHA_DARK : MAP_FILL_ALPHA)

/** A legend swatch at the map's own fill alpha, so the key matches the map. */
export function legendSwatch(hex, theme = 'light') {
  const [r, g, b] = rgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${fillAlpha(theme)})`
}

export function colorForSection(feature, status, colorMode, est, heatScale) {
  if (colorMode === 'category') return feature.properties.color || CATEGORY_COLOR.other
  if (colorMode === 'price' && est && heatScale) return heatScale.colorFor(est)
  if (status.free) return STATUS_COLOR.free
  if (status.changesInMin != null && status.changesInMin <= SOON_MIN) return STATUS_COLOR.soon
  return STATUS_COLOR.paid
}

/**
 * The feature-state for one section. Selection is NOT part of it: the selected
 * outline is its own layer (filtered by id), so selecting never overwrites the
 * fill that carries the price.
 * @returns {{fill:string, stroke:string}}
 */
export function sectionPaint(feature, status, colorMode, est, heatScale, theme = 'light') {
  const fill = colorForSection(feature, status, colorMode, est, heatScale)
  return { fill, stroke: theme === 'dark' ? lighten(fill) : darken(fill) }
}

/** Filter for the selected-outline layers. Numeric feature ids; -1 = none. */
export function selectedFilter(num) {
  return ['==', ['id'], num == null ? -1 : num]
}

/**
 * Layer specs for the zones source, bottom to top. MapView stacks them over the
 * basemap's streets and under its labels, and close up lays a copy of its
 * buildings on top (see zoneStacking).
 */
export function zoneLayers(source, theme = 'light', selectedNum = null) {
  const dark = theme === 'dark'
  return [
    {
      id: 'zones-fill',
      type: 'fill',
      source,
      paint: {
        'fill-color': ['coalesce', ['feature-state', 'fill'], 'rgba(0,0,0,0)'],
        'fill-opacity': fillAlpha(theme),
      },
    },
    {
      id: 'zones-line',
      type: 'line',
      source,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['coalesce', ['feature-state', 'stroke'], dark ? '#9aa4b0' : '#667085'],
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.35, 14, 0.8, 17, 1.6],
        'line-opacity': 0.9,
      },
    },
    {
      id: 'zones-selected-casing',
      type: 'line',
      source,
      filter: selectedFilter(selectedNum),
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': dark ? '#05070a' : '#ffffff',
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 4, 16, 8],
      },
    },
    {
      id: 'zones-selected',
      type: 'line',
      source,
      filter: selectedFilter(selectedNum),
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': dark ? '#ffffff' : '#0b1f33',
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 2, 16, 3.5],
      },
    },
  ]
}
